import CustomImageGallery from './ImageGallery';
import EventCard from './EventCard';
import { Link } from 'react-router-dom';
import { useGetHomeContent, useGetEventsContent } from '../services';
import { Loading, Error } from '../shared/components';

const MainPage = () => {
  const {
    homeData,
    isLoading: homeLoading,
    error: homeError,
  } = useGetHomeContent();
  const { upcomingEvents, isLoading: eventsLoading } = useGetEventsContent();

  // Loading state
  if (homeLoading || eventsLoading) {
    return <Loading />;
  }

  // Error state
  if (homeError) {
    return <Error message={homeError} />;
  }

  // Get the first upcoming event for EventCard
  const firstUpcomingEvent = upcomingEvents?.[0]
    ? {
        topic: upcomingEvents[0].title,
        day: upcomingEvents[0].day,
        time: `${upcomingEvents[0].startTimeStr || ''} - ${
          upcomingEvents[0].endTimeStr || ''
        }`,
        webinarLink: upcomingEvents[0].webinarLink || undefined,
        description: upcomingEvents[0].desc || undefined,
        speaker: upcomingEvents[0].speaker,
        linkedIn: upcomingEvents[0].linkedIn || undefined,
        date: upcomingEvents[0].dateStr,
      }
    : undefined;

  const getGalleryImages = () => {
    if (
      upcomingEvents?.[0]?.fileUrl &&
      upcomingEvents?.[0]?.fileUrl.length > 0
    ) {
      return upcomingEvents[0].fileUrl;
    }
    return ['/1.jpg', '/2.jpg', '/3.jpg', '/4.jpg', '/5.jpg'];
  };

  return (
    <div className="py-4 sm:py-6 lg:py-8">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Title and Description Section */}
        <div className="mb-8 sm:mb-12 lg:mb-16">
          <div className="w-full text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 text-primary-500">
              {homeData?.home_title_first?.[0]?.home_title_first_title ||
                'Welcome to VLSI Club'}
            </h2>
            <div className="w-16 sm:w-20 h-0.5 rounded bg-primary-500 mx-auto mb-6 sm:mb-8"></div>
            <div className="text-base sm:text-lg lg:text-xl leading-relaxed mb-6 text-secondary-700 whitespace-pre-line">
              {homeData?.home_title_first?.[0]?.home_title_second_description ||
                'We are a passionate community of VLSI enthusiasts, engineers, and researchers dedicated to advancing the field of Very Large Scale Integration design and technology.\n\nJoin us to explore cutting-edge semiconductor technologies, participate in hands-on workshops, attend expert talks, and collaborate on innovative projects that shape the future of chip design and digital systems.'}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-8">
              {homeData?.home_title_first?.slice(1, 4).map((item, index) => (
                <div
                  key={index}
                  className="bg-secondary-50 p-4 sm:p-6 rounded-lg"
                >
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-primary-500">
                    {item.home_title_first_title}
                  </h3>
                  <p className="text-sm sm:text-base text-secondary-700">
                    {item.home_title_second_description}
                  </p>
                </div>
              )) || (
                // Fallback content
                <>
                  <div className="bg-secondary-50 p-4 sm:p-6 rounded-lg">
                    <h3 className="text-lg sm:text-xl font-semibold mb-2 text-primary-500">
                      Learn
                    </h3>
                    <p className="text-sm sm:text-base text-secondary-700">
                      Master EDA tools, HDL programming, and advanced design
                      methodologies
                    </p>
                  </div>
                  <div className="bg-secondary-50 p-4 sm:p-6 rounded-lg">
                    <h3 className="text-lg sm:text-xl font-semibold mb-2 text-primary-500">
                      Network
                    </h3>
                    <p className="text-sm sm:text-base text-secondary-700">
                      Connect with industry professionals and fellow VLSI
                      enthusiasts
                    </p>
                  </div>
                  <div className="bg-secondary-50 p-4 sm:p-6 rounded-lg">
                    <h3 className="text-lg sm:text-xl font-semibold mb-2 text-primary-500">
                      Innovate
                    </h3>
                    <p className="text-sm sm:text-base text-secondary-700">
                      Work on real-world projects and contribute to
                      semiconductor advancement
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Left Side - Image Gallery */}
          <div className="lg:col-span-2">
            <div className="h-full min-h-[300px] sm:min-h-[400px] border-2 border-primary-500 rounded-lg overflow-hidden shadow-lg">
              <CustomImageGallery
                images={getGalleryImages()}
                autoSlide={true}
                slideInterval={4000}
              />
            </div>
          </div>

          {/* Right Side - Event Card */}
          <div className="lg:col-span-1">
            <div className="h-full min-h-[200px] sm:min-h-[300px]">
              <EventCard eventDetails={firstUpcomingEvent} />
            </div>
          </div>
        </div>

        {/* Our Sponsors Section */}
        <div className="w-full mt-12 sm:mt-16 lg:mt-20">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-primary-500">
              {homeData?.home_title_second?.[0]?.home_title_second_title ||
                'Our Sponsors'}
            </h2>
            <div className="w-16 sm:w-20 h-0.5 rounded bg-primary-500 mx-auto mb-4"></div>
            <p className="text-base sm:text-lg text-secondary-700 mx-auto">
              {homeData?.home_title_second?.[0]
                ?.home_title_second_description ||
                "We're grateful to our industry partners who support our mission to advance VLSI education and innovation."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {/* VeriFast Technologies */}
            <div className="bg-white border border-secondary-50 rounded-lg p-6 sm:p-8 shadow-lg text-center">
              <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-4">
                <img
                  src="/verifast.png"
                  alt="VeriFast Technologies Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-lg sm:text-xl font-bold mb-3 text-primary-500">
                VeriFast Technologies Inc.
              </h3>
              <p className="text-sm sm:text-base text-secondary-700 mb-4">
                Founded in 2012, VeriFast Technologies was born out of a passion
                for excellence and a commitment to providing cutting-edge ASIC,
                SoC & FPGA Verification. Over the years, VeriFast has grown into
                a leading provider of verification solutions, trusted by some of
                the most influential companies in the industry.
              </p>
              <div className="text-center">
                <a
                  href="https://www.linkedin.com/company/verifast-technologies/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-500 hover:opacity-80 underline cursor-pointer transition-all duration-200 hover:scale-105"
                >
                  LinkedIn Profile
                </a>
              </div>
            </div>

            {/* ASICSoft Inc */}
            <div className="bg-white border border-secondary-50 rounded-lg p-6 sm:p-8 shadow-lg text-center">
              <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-4">
                <img
                  src="/asicsoft.jpg"
                  alt="ASICSoft Inc Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-lg sm:text-xl font-bold mb-3 text-primary-500">
                ASICSoft Inc
              </h3>
              <p className="text-sm sm:text-base text-secondary-700 mb-4">
                ASICSoft is the leader in staffing and consulting for
                Engineering and IT projects. Launched in 2006 ASICSoft has
                provided high quality talent and consulting services to leading
                companies throughout the United States. Our clients range from
                giants to start-ups in semiconductor, networking, multimedia,
                consumer electronics, defense/aerospace, medical, cloud, mobile,
                and beyond.
              </p>
              <div className="text-center">
                <a
                  href="https://www.linkedin.com/company/asicsoft/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-500 hover:opacity-80 underline cursor-pointer transition-all duration-200 hover:scale-105"
                >
                  LinkedIn Profile
                </a>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-sm sm:text-base text-secondary-700">
              Interested in becoming a sponsor?{' '}
              <Link
                to="/collaboration"
                className="text-primary-500 underline hover:opacity-80 font-medium transition-all duration-200 hover:scale-105"
              >
                Learn more about collaboration opportunities
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainPage;
